import { useEffect } from 'react';
import { capturesAPI } from '../services/api';

/**
 * Makes every template's newsletter form actually submit.
 *
 * Twenty storefront layouts render a footer signup whose <form> does
 * `onSubmit={(e) => e.preventDefault()}` and nothing else — shoppers have
 * been typing their email into a dead box on every store. Instead of
 * hand-wiring each layout (and every future one), this listens for submits
 * at the document in the capture phase, so it runs before the layout's
 * own no-op handler, and treats a form whose ONLY field is a single email
 * input as a newsletter signup: POST /store/captures, clear the box, flip
 * the button to "Subscribed ✓" for a moment.
 *
 * Forms with any other field (checkout, contact, reviews, search) are not
 * touched. A layout that wires its own handler can opt out with
 * `data-newsletter-wired` on the form.
 */
export function NewsletterFormCapture() {
  useEffect(() => {
    const onSubmit = (ev: Event) => {
      const form = ev.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.newsletterWired !== undefined) return;

      const emails = form.querySelectorAll<HTMLInputElement>('input[type="email"]');
      const otherFields = form.querySelectorAll(
        'input:not([type="email"]):not([type="hidden"]):not([type="submit"]):not([type="button"]), textarea, select',
      );
      if (emails.length !== 1 || otherFields.length > 0) return;

      const input = emails[0];
      const email = input.value.trim();
      if (!email || !/^\S+@\S+\.\S+$/.test(email)) return;

      capturesAPI
        .submit({
          type: 'newsletter',
          email,
          context: { source: 'template_footer', path: window.location.pathname },
        })
        .catch(() => {
          /* best-effort — the visitor has already been thanked */
        });

      input.value = '';
      const button = form.querySelector<HTMLButtonElement>('button[type="submit"], button:not([type])');
      if (button) {
        const original = button.innerHTML;
        button.textContent = 'Subscribed ✓';
        button.disabled = true;
        window.setTimeout(() => {
          button.innerHTML = original;
          button.disabled = false;
        }, 3000);
      }
    };

    document.addEventListener('submit', onSubmit, true);
    return () => document.removeEventListener('submit', onSubmit, true);
  }, []);

  return null;
}
